import { useState } from 'react';

export default function HomePage() {
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [consultationStatus, setConsultationStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleDocumentUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsUploading(true);
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Convert FormData to URL-encoded format
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        // For checkboxes, only include if checked
        if (key === 'privacy_consent') {
          if (value === 'on') {
            urlEncodedData.append(key, 'yes');
          }
        } else {
          urlEncodedData.append(key, value);
        }
      } else {
        // For file uploads, mark as uncollectable
        urlEncodedData.append(key, 'Uncollectable');
      }
    });
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d48hqlnjsic9gsa4uvvg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });
      
      if (response.ok) {
        alert('Documents uploaded successfully! We will review them and contact you soon.');
        setShowUploadForm(false);
        form.reset();
        setUploadProgress(0);
      } else {
        alert('Upload failed. Please try again.');
      }
    } catch (error) {
      alert('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleConsultationSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setConsultationStatus('submitting');
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Convert FormData to URL-encoded format
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        // For checkboxes, only include if checked
        if (key === 'marketing_consent') {
          if (value === 'yes') {
            urlEncodedData.append(key, 'yes');
          }
        } else {
          urlEncodedData.append(key, value);
        }
      }
    });
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4igvs2jg2jl50j1sdd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });
      
      if (response.ok) {
        setConsultationStatus('success');
        form.reset();
        setTimeout(() => {
          setShowConsultationForm(false);
          setConsultationStatus('idle');
        }, 3000);
      } else {
        setConsultationStatus('error');
      }
    } catch (error) {
      setConsultationStatus('error');
    }
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const formData = new FormData(e.currentTarget);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/contact_form_url', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        alert('Message sent successfully! We will get back to you soon.');
        setShowContactForm(false);
        (e.target as HTMLFormElement).reset();
      } else {
        alert('Submission failed. Please try again.');
      }
    } catch (error) {
      alert('Submission failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-800">
                Kinco Financial Solutions
              </h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#services" className="text-black hover:text-gray-700 font-medium cursor-pointer">Services</a>
              <a href="#incentives" className="text-black hover:text-gray-700 font-medium cursor-pointer">Incentives</a>
              <a href="#about" className="text-black hover:text-gray-700 font-medium cursor-pointer">About</a>
              <a href="#contact" className="text-black hover:text-gray-700 font-medium cursor-pointer">Contact</a>
            </nav>
            <div className="flex items-center space-x-4">
              <a href="tel:866-202-0964" className="text-black font-semibold hover:text-gray-700 cursor-pointer">
                <i className="ri-phone-line mr-2"></i>
                866-202-0964
              </a>
              <button 
                onClick={() => setShowUploadForm(true)}
                className="bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Upload Documents
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20" style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/0a8dfd2d278cb92e9d1ba7348aa4c4ba.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="bg-gray-800/70 px-6 py-4 rounded-lg mb-8">
              <h2 className="text-5xl font-bold text-white mb-4">
                Expert Tax Services
              </h2>
              <p className="text-xl font-bold text-white">
                Professional tax preparation and financial consulting services nationwide. File with us and win cash prize or vacation!
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => setShowConsultationForm(true)}
                className="bg-blue-900 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Free Consultation
              </button>
              <button 
                onClick={() => setShowUploadForm(true)}
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Upload Tax Documents
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-white mb-4">Our Tax Services</h3>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto">
              Comprehensive tax preparation and financial consulting services designed to maximize your returns and minimize your stress.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-text-line text-2xl text-blue-900"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Individual Tax Preparation</h4>
              <p className="text-gray-600 mb-6">Expert preparation of personal tax returns with maximum deduction identification and accuracy guarantee.</p>
              <button 
                onClick={() => setShowConsultationForm(true)}
                className="text-blue-900 font-semibold hover:text-blue-700 cursor-pointer"
              >
                Learn More →
              </button>
            </div>

            <div className="bg-white p-8 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-building-line text-2xl text-green-700"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Business Tax Services</h4>
              <p className="text-gray-600 mb-6">Complete business tax preparation, quarterly filings, and strategic tax planning for all business types.</p>
              <button 
                onClick={() => setShowConsultationForm(true)}
                className="text-blue-900 font-semibold hover:text-blue-700 cursor-pointer"
              >
                Learn More →
              </button>
            </div>

            <div className="bg-white p-8 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-purple-700"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Audit Protection</h4>
              <p className="text-gray-600 mb-6">Professional audit assistance and representation to protect your interests with the IRS.</p>
              <button 
                onClick={() => setShowConsultationForm(true)}
                className="text-blue-900 font-semibold hover:text-blue-700 cursor-pointer"
              >
                Learn More →
              </button>
            </div>

            <div className="bg-white p-8 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-calculator-line text-2xl text-orange-700"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Tax Planning</h4>
              <p className="text-gray-600 mb-6">Strategic year-round tax planning to minimize your tax liability and maximize savings.</p>
              <button 
                onClick={() => setShowConsultationForm(true)}
                className="text-blue-900 font-semibold hover:text-blue-700 cursor-pointer"
              >
                Learn More →
              </button>
            </div>

            <div className="bg-white p-8 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-book-line text-2xl text-red-700"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Bookkeeping Services</h4>
              <p className="text-gray-600 mb-6">Professional bookkeeping and financial record management to keep your finances organized.</p>
              <button 
                onClick={() => setShowConsultationForm(true)}
                className="text-blue-900 font-semibold hover:text-blue-700 cursor-pointer"
              >
                Learn More →
              </button>
            </div>

            <div className="bg-white p-8 rounded-xl hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-upload-cloud-line text-2xl text-teal-700"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Secure Document Upload</h4>
              <p className="text-gray-600 mb-6">Safe and secure document upload system for convenient tax document submission.</p>
              <button 
                onClick={() => setShowUploadForm(true)}
                className="text-blue-900 font-semibold hover:text-blue-700 cursor-pointer"
              >
                Upload Now →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Incentives Section */}
      <section id="incentives" className="py-20 bg-gray-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-white mb-4">Exclusive Client Incentives</h3>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto">
              File your taxes with Kinco Financial Solutions and become eligible for amazing rewards!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-3xl text-green-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Win Cash Prize</h4>
              <p className="text-black mb-6">Win a cash prize when you file your taxes with us. Multiple winners selected monthly!</p>
              <div className="text-3xl font-bold text-gray-900 mb-4">Cash Prize</div>
              <button 
                onClick={() => setShowConsultationForm(true)}
                className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Started
              </button>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-plane-line text-3xl text-blue-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">A Dream Vacation</h4>
              <p className="text-black mb-6">Win a dream vacation to exotic destinations. Your dream getaway awaits!</p>
              <div className="text-3xl font-bold text-gray-900 mb-4">Vacation</div>
              <button 
                onClick={() => setShowConsultationForm(true)}
                className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Learn More
              </button>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-gift-line text-3xl text-purple-600"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Refer a Friend!</h4>
              <p className="text-black mb-6">Access to exclusive discounts, premium services, and special offers available only to our clients.</p>
              <div className="text-3xl font-bold text-gray-900 mb-2">Referral</div>
              <button 
                onClick={() => setShowConsultationForm(true)}
                className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Join Now
              </button>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-white mb-6">
              <strong>How it works:</strong> Simply file your taxes with us and you're automatically entered into our monthly drawings.
            </p>
            <button 
              onClick={() => setShowUploadForm(true)}
              className="bg-blue-900 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Start Your Tax Filing Today
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold text-gray-900 mb-6">Why Choose Kinco Financial Solutions?</h3>
              <p className="text-lg text-gray-600 mb-8">
                With years of experience in tax preparation and financial consulting, we provide personalized service that puts your financial success first. Our nationwide service ensures you get expert help wherever you are.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Certified Tax Professionals</h4>
                    <p className="text-gray-600">Our team consists of certified tax professionals with extensive experience in tax law and financial planning.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Nationwide Service</h4>
                    <p className="text-gray-600">We serve clients across the United States with the same high-quality service and attention to detail.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Secure & Confidential</h4>
                    <p className="text-gray-600">Your financial information is protected with bank-level security and strict confidentiality protocols.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Year-Round Support</h4>
                    <p className="text-gray-600">We provide ongoing support and advice throughout the year, not just during tax season.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/b8b34086e78d19e91aeae5700269efdf/fd16da40881cda3aefb781f62f52ab22.png"
                alt="Kinco Financial Solutions team of tax professionals"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h3>
          <p className="text-xl text-gray-100 mb-8 max-w-3xl mx-auto">
            Join thousands of satisfied clients who trust Kinco Financial Solutions for their tax needs. Schedule your free consultation today and discover how much you could save.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setShowConsultationForm(true)}
              className="bg-blue-900 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule Free Consultation
            </button>
            <button 
              onClick={() => setShowUploadForm(true)}
              className="bg-white text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Upload Documents Now
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-white mb-4">Contact Us</h3>
            <p className="text-xl text-gray-100">Get in touch with our tax professionals today</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-phone-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Call Us</h4>
              <p className="text-black mb-4">Speak with a tax professional</p>
              <a href="tel:866-202-0964" className="text-2xl text-black hover:text-gray-700 cursor-pointer">
                866-202-0964
              </a>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-mail-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Email Us</h4>
              <p className="text-black mb-4">Send us your questions</p>
              <a href="mailto:k.smith@kincosolutions.com" className="text-lg font-semibold text-black hover:text-gray-700 cursor-pointer break-all">
                k.smith@kincosolutions.com
              </a>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg text-center md:col-span-2 lg:col-span-1">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Service Area</h4>
              <p className="text-black mb-4">We serve clients nationwide</p>
              <p className="text-lg font-semibold text-black">United States</p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button 
              onClick={() => setShowContactForm(true)}
              className="bg-blue-900 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Send Us a Message
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h4 className="text-2xl font-bold mb-4">
                Kinco Financial Solutions
              </h4>
              <p className="text-gray-100 mb-6">
                Professional tax preparation and financial consulting services with exclusive client incentives. Serving clients nationwide with expertise you can trust.
              </p>
              <div className="flex space-x-4">
                <a href="tel:866-202-0964" className="text-gray-100 hover:text-white cursor-pointer">
                  <i className="ri-phone-line text-xl"></i>
                </a>
                <a href="mailto:k.smith@kincosolutions.com" className="text-gray-100 hover:text-white cursor-pointer">
                  <i className="ri-mail-line text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h5 className="text-lg font-semibold mb-4">Services</h5>
              <ul className="space-y-2">
                <li><a href="#services" className="text-gray-100 hover:text-white cursor-pointer">Individual Tax Prep</a></li>
                <li><a href="#services" className="text-gray-100 hover:text-white cursor-pointer">Business Tax Services</a></li>
                <li><a href="#services" className="text-gray-100 hover:text-white cursor-pointer">Tax Planning</a></li>
                <li><a href="#services" className="text-gray-100 hover:text-white cursor-pointer">Audit Protection</a></li>
              </ul>
            </div>

            <div>
              <h5 className="text-lg font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2">
                <li><a href="#incentives" className="text-gray-100 hover:text-white cursor-pointer">Client Incentives</a></li>
                <li><a href="#about" className="text-gray-100 hover:text-white cursor-pointer">About Us</a></li>
                <li><a href="#contact" className="text-gray-100 hover:text-white cursor-pointer">Contact</a></li>
                <li>
                  <button 
                    onClick={() => setShowUploadForm(true)}
                    className="text-gray-100 hover:text-white cursor-pointer"
                  >
                    Upload Documents
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-500 mt-8 pt-8 text-center">
            <p className="text-gray-200">
              © 2024 Kinco Financial Solutions. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-gray-100 hover:text-white ml-2 cursor-pointer">
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Document Upload Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-900">Secure Document Upload</h3>
                <button 
                  onClick={() => setShowUploadForm(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleDocumentUpload} className="p-6 space-y-6" data-readdy-form id="document_upload">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input 
                  type="text" 
                  name="full_name" 
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your full name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input 
                    type="email" 
                    name="email" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tax Year</label>
                <select 
                  name="tax_year" 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                >
                  <option value="2025">2025</option>
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                  <option value="2022">2022</option>
                  <option value="2021">2021</option>
                  <option value="2020">2020</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Document Type</label>
                <select 
                  name="document_type" 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                >
                  <option value="W-2">W-2 Forms</option>
                  <option value="1099">1099 Forms</option>
                  <option value="1098">1098 Forms</option>
                  <option value="receipts">Receipts & Deductions</option>
                  <option value="business">Business Documents</option>
                  <option value="other">Other Tax Documents</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Documents *</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-600 mb-2">Drag and drop your files here, or click to browse</p>
                  <input 
                    type="file" 
                    name="documents" 
                    multiple 
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    required
                    className="hidden"
                    id="file-upload"
                  />
                  <label 
                    htmlFor="file-upload" 
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer inline-block"
                  >
                    Choose Files
                  </label>
                  <p className="text-sm text-gray-500 mt-2">Supported formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB each)</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                <textarea 
                  name="notes" 
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Any additional information or special instructions..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              <div className="flex items-start">
                <input 
                  type="checkbox" 
                  name="privacy_consent" 
                  required 
                  className="mt-1 mr-3"
                />
                <label className="text-sm text-gray-600">
                  I consent to the secure processing of my tax documents and understand that all information will be kept confidential in accordance with IRS regulations and privacy laws. *
                </label>
              </div>

              <div className="flex gap-4">
                <button 
                  type="button"
                  onClick={() => setShowUploadForm(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isUploading}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {isUploading ? 'Uploading...' : 'Upload Documents'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Consultation Form Modal */}
      {showConsultationForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-900">Free Consultation Request</h3>
                <button 
                  onClick={() => {
                    setShowConsultationForm(false);
                    setConsultationStatus('idle');
                  }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleConsultationSubmit} className="p-6 space-y-6" data-readdy-form id="consultation_request">
              {consultationStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                    <span>Consultation request submitted! We will contact you within 24 hours.</span>
                  </div>
                </div>
              )}
              
              {consultationStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-xl mr-2"></i>
                    <span>Submission failed. Please try again.</span>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                  <input 
                    type="text" 
                    name="first_name" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                  <input 
                    type="text" 
                    name="last_name" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input 
                    type="email" 
                    name="email" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Needed</label>
                <select 
                  name="service_type" 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                >
                  <option value="individual">Individual Tax Preparation</option>
                  <option value="business">Business Tax Services</option>
                  <option value="planning">Tax Planning</option>
                  <option value="audit">Audit Protection</option>
                  <option value="bookkeeping">Bookkeeping Services</option>
                  <option value="consultation">General Consultation</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input type="radio" name="contact_method" value="phone" className="mr-2" />
                    Phone
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="contact_method" value="email" className="mr-2" />
                    Email
                  </label>
                  <label className="flex items-center">
                    <input type="radio" name="contact_method" value="both" className="mr-2" defaultChecked />
                    Both
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Best Time to Contact</label>
                <select 
                  name="best_time" 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                >
                  <option value="morning">Morning (9 AM - 12 PM)</option>
                  <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                  <option value="evening">Evening (5 PM - 8 PM)</option>
                  <option value="anytime">Anytime</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tell us about your tax situation</label>
                <textarea 
                  name="message" 
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Describe your tax needs, questions, or any specific concerns..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              <div className="flex items-start">
                <input 
                  type="checkbox" 
                  name="marketing_consent" 
                  value="yes"
                  className="mt-1 mr-3"
                />
                <label className="text-sm text-gray-600">
                  I would like to receive updates about tax law changes, special offers, and incentive program notifications.
                </label>
              </div>

              <div className="flex gap-4">
                <button 
                  type="button"
                  onClick={() => {
                    setShowConsultationForm(false);
                    setConsultationStatus('idle');
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={consultationStatus === 'submitting'}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {consultationStatus === 'submitting' ? 'Submitting...' : 'Request Consultation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-900">Send Us a Message</h3>
                <button 
                  onClick={() => setShowContactForm(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleContactSubmit} className="p-6 space-y-6" data-readdy-form id="contact_form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                  <input 
                    type="text" 
                    name="first_name" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                  <input 
                    type="text" 
                    name="last_name" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                <input 
                  type="email" 
                  name="email" 
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input 
                  type="tel" 
                  name="phone" 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                <input 
                  type="text" 
                  name="subject" 
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="How can we help you?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea 
                  name="message" 
                  rows={6}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Please share your questions or comments..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">Maximum 500 characters</p>
              </div>

              <div className="flex gap-4">
                <button 
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Send Message
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
